import type {
  GridApiPremium,
  GridEventListener,
  GridRowSelectionModel,
  GridRowSelectionPropagation,
} from "@mui/x-data-grid-premium";
import { GRID_TREE_DATA_GROUPING_FIELD } from "@mui/x-data-grid-premium";
import type { RefObject } from "react";
import { useCallback, useEffect, useMemo } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import { useGridGroupsCollapsed } from "@entrilia/shared/components/grid/gridHooks";
import { getVisibleRowIds, selectNexRow, selectPreviousRow } from "@entrilia/shared/utilities/dataGridApiHelper";
import type { DocumentInfo } from "../../../../../api/types/documentCollectionTypes";
import { fundLevelColumns, fundLevelGroupingColumn, getFundLevelRows } from "./documentsTableDataProvider";

const rowSelectionPropagation: GridRowSelectionPropagation = {
  parents: false,
  descendants: false,
};

interface Props {
  apiRef: RefObject<GridApiPremium | null>;
  documents: DocumentInfo[];
  showPublishingStatus: boolean;
  showPortalPublishStatus: boolean;
  investorPortalEnabled: boolean;
  showFundName: boolean;
  onSelectionChange: (selection: GridRowSelectionModel) => void;
}

const DocumentsTableFundLevel = ({
  documents,
  showPublishingStatus,
  showPortalPublishStatus,
  investorPortalEnabled,
  showFundName,
  onSelectionChange,
  apiRef,
}: Props) => {
  const { isGroupExpanded } = useGridGroupsCollapsed(apiRef);

  const rows = useMemo(() => getFundLevelRows(documents), [documents]);

  useEffect(() => {
    const firstRowId = getVisibleRowIds(apiRef)[0];
    if (!firstRowId) {
      return;
    }
    const selectTimeoutId = setTimeout(() => {
      apiRef.current?.setRowSelectionModel({ ids: new Set([firstRowId]), type: "include" });
      apiRef.current?.setCellFocus(firstRowId, GRID_TREE_DATA_GROUPING_FIELD);
    });
    return () => clearTimeout(selectTimeoutId);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  useEffect(() => {
    return apiRef.current?.subscribeEvent("rowSelectionChange", (newSelection) => {
      onSelectionChange(newSelection);
    });
  }, [apiRef, onSelectionChange]);

  const handleCellKeyDown: GridEventListener<"cellKeyDown"> = useCallback(
    (_, e) => {
      if (e.code !== "ArrowUp" && e.code !== "ArrowDown") {
        return;
      }

      if (e.code === "ArrowDown") {
        selectNexRow(apiRef);
      } else if (e.code === "ArrowUp") {
        selectPreviousRow(apiRef);
      }

      e.stopPropagation();
      e.preventDefault();
    },
    [apiRef]
  );

  return (
    <DataGrid
      apiRef={apiRef}
      treeData
      getTreeDataPath={(row) => [row.group, row.investor]}
      groupingColDef={fundLevelGroupingColumn}
      columns={fundLevelColumns}
      rows={rows}
      onCellKeyDown={handleCellKeyDown}
      isGroupExpandedByDefault={isGroupExpanded}
      disableRowSelectionOnClick={false}
      disableMultipleRowSelection
      rowSelectionPropagation={rowSelectionPropagation}
      disableColumnSorting
      columnVisibilityModel={{
        publishingStatus: showPublishingStatus,
        publishToInvestorPortal: showPortalPublishStatus,
        fundName: showFundName,
        documentAccess: investorPortalEnabled,
        isViewed: investorPortalEnabled,
      }}
      sx={{
        ".MuiDataGrid-row": {
          "&:hover": {
            cursor: "pointer",
          },
        },
      }}
    />
  );
};

export default DocumentsTableFundLevel;
